import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw, RouteLocationNormalizedLoaded } from "vue-router";
import Clientes from "@/views/Clientes.vue";
import BienesCliente from "@/views/BienesCliente.vue";
import EditarCliente from "@/views/EditarCliente.vue";
import Login from "@/views/Login.vue";
import store from "@/store";
import { ActivityApi } from "@/api/activity";
import type { BreadcrumbItem } from "./breadcrumbs";
import { requiredModuleForPath } from "@/config/modules";

const homeCrumb = (label = "Inicio"): BreadcrumbItem => ({
  label,
  to: "/dashboard",
});
const personasCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Personas" },
];
const clientesCrumbs = (): BreadcrumbItem[] => [
  ...personasCrumbs(),
  { label: "Clientes / Personas", to: "/clientes" },
];
const ventasCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Ventas", to: "/ventas" },
];
const presupuestosCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Ventas", to: "/ventas" },
  { label: "Presupuestos", to: "/presupuestos" },
];
const liquidosProductoCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Líquido Producto", to: "/liquidos-producto" },
];
const comprasCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Compras", to: "/compras" },
];
const ivaDigitalCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Contabilidad" },
  { label: "IVA Digital", to: "/iva-digital" },
];
const remitosCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Remitos", to: "/remitos" },
];
const parametrosCrumbs = (): BreadcrumbItem[] => [
  homeCrumb(),
  { label: "Parámetros" },
];

const cuentaCorrienteCrumbs = (
  route: RouteLocationNormalizedLoaded,
  lastLabel = "Cuenta corriente"
): BreadcrumbItem[] => [
  ...clientesCrumbs(),
  {
    label: "Cuenta corriente",
    to: `/clientes/${route.params.id}/cuenta-corriente`,
  },
  { label: lastLabel },
];

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: (to) => {
      const logueado = store.getters.isAuthenticated;
      return {
        path: logueado ? "/dashboard" : "/login",
        query: to.query,
      };
    },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/views/DashboardMovimientos.vue"),
    meta: {
      title: "Dashboard",
      breadcrumbs: [{ label: "Inicio" }],
    },
  },
  {
    path: "/perfil",
    name: "Perfil",
    component: () => import("@/views/ProfileView.vue"),
    meta: {
      title: "Perfil",
      breadcrumbs: [homeCrumb(), { label: "Perfil" }],
    },
  },
  {
    path: "/perfil/ayuda-afip",
    name: "AfipTutorial",
    component: () => import("@/views/AfipTutorialView.vue"),
    meta: {
      title: "Tutorial AFIP / ARCA",
      breadcrumbs: [homeCrumb(), { label: "Perfil", to: "/perfil" }, { label: "Tutorial AFIP / ARCA" }],
    },
  },
  {
    path: "/perfil/certificados-afip",
    name: "CertificadoAFIP",
    component: () => import("@/views/CertificadoAFIPView.vue"),
    meta: {
      title: "Generar clave privada y CSR",
      breadcrumbs: [homeCrumb(), { label: "Perfil", to: "/perfil" }, { label: "Generar CSR" }],
    },
  },
  {
    path: "/clientes",
    name: "Clientes",
    component: Clientes,
    meta: {
      title: "Gestión de clientes",
      breadcrumbs: [...clientesCrumbs()],
    },
  },
  {
    path: "/clientes/nuevo",
    name: "NuevoCliente",
    component: EditarCliente,
    meta: {
      title: "Nuevo cliente",
      breadcrumbs: [...clientesCrumbs(), { label: "Nuevo cliente" }],
    },
  },
  {
    path: "/clientes/:id/editar",
    name: "EditarCliente",
    component: EditarCliente,
    meta: {
      title: "Editar cliente",
      breadcrumbs: [...clientesCrumbs(), { label: "Editar cliente" }],
    },
  },
  {
    path: "/bienes/:id/",
    name: "BienesCliente",
    component: BienesCliente,
    meta: {
      title: "Bienes del cliente",
      requiresAmortizaciones: true,
      breadcrumbs: [...clientesCrumbs(), { label: "Bienes" }],
    },
  },
  {
    path: "/clientes/:id/bienes/nuevo",
    name: "NuevoBien",
    component: () => import("@/views/EditarBien.vue"),
    meta: {
      title: "Nuevo bien",
      requiresAmortizaciones: true,
      breadcrumbs: (route: RouteLocationNormalizedLoaded) => [
        ...clientesCrumbs(),
        { label: "Bienes", to: `/bienes/${route.params.id}` },
        { label: "Nuevo bien" },
      ],
    },
  },
  {
    path: "/clientes/:id/bienes/:bienId/editar",
    name: "EditarBien",
    component: () => import("@/views/EditarBien.vue"),
    meta: {
      title: "Editar bien",
      requiresAmortizaciones: true,
      breadcrumbs: (route: RouteLocationNormalizedLoaded) => [
        ...clientesCrumbs(),
        { label: "Bienes", to: `/bienes/${route.params.id}` },
        { label: "Editar bien" },
      ],
    },
  },
  {
    path: "/clientes/:id/cuenta-corriente",
    name: "CuentaCorriente",
    component: () => import("@/views/CuentaCorriente.vue"),
    meta: {
      title: "Cuenta corriente",
      breadcrumbs: (route: RouteLocationNormalizedLoaded) => [
        ...clientesCrumbs(),
        { label: "Cuenta corriente" },
      ],
    },
  },
  {
    path: "/clientes/:id/nuevo-recibo",
    name: "NuevoRecibo",
    component: () => import("@/views/NuevoRecibo.vue"),
    meta: {
      title: "Nuevo recibo",
      breadcrumbs: (route: RouteLocationNormalizedLoaded) =>
        cuentaCorrienteCrumbs(route, "Nuevo recibo"),
    },
  },
  {
    path: "/clientes/:id/nueva-orden-pago",
    name: "NuevaOrdenPago",
    component: () => import("@/views/NuevaOrdenPago.vue"),
    meta: {
      title: "Nueva orden de pago",
      breadcrumbs: (route: RouteLocationNormalizedLoaded) =>
        cuentaCorrienteCrumbs(route, "Nueva orden de pago"),
    },
  },
  {
    path: "/ordenes-pago",
    name: "OrdenesPago",
    component: () => import("@/views/OrdenesPagoLista.vue"),
    meta: {
      title: "Órdenes de pago",
      breadcrumbs: [...comprasCrumbs(), { label: "Órdenes de pago" }],
    },
  },
  {
    path: "/ordenes-pago/nueva",
    name: "OrdenesPagoNueva",
    component: () => import("@/views/NuevaOrdenPago.vue"),
    meta: {
      title: "Nueva orden de pago",
      breadcrumbs: [...comprasCrumbs(), { label: "Órdenes de pago", to: "/ordenes-pago" }, { label: "Nueva" }],
    },
  },
  {
    path: "/ordenes-pago/:id",
    name: "OrdenesPagoDetalle",
    component: () => import("@/views/OrdenPagoDetalle.vue"),
    meta: {
      title: "Detalle de orden de pago",
      breadcrumbs: [...comprasCrumbs(), { label: "Órdenes de pago", to: "/ordenes-pago" }, { label: "Detalle" }],
    },
  },
  {
    path: "/recibos/:id/editar-movimientos",
    name: "EditarMovimientosRecibo",
    component: () => import("@/views/EditarMovimientosRecibo.vue"),
    meta: {
      title: "Editar movimientos del recibo",
      breadcrumbs: [
        homeCrumb(),
        { label: "Cuenta corriente" },
        { label: "Editar recibo" },
      ],
    },
  },
  {
    path: "/grupos",
    name: "Grupos",
    component: () => import("@/views/Grupos.vue"),
    meta: {
      title: "Grupos de clientes",
      breadcrumbs: [...personasCrumbs(), { label: "Grupos" }],
    },
  },
  {
    path: "/movimientostipo",
    name: "MovimientosTipo",
    component: () => import("@/views/MovimientosTipo.vue"),
    meta: {
      title: "Tipos de movimiento",
      breadcrumbs: [
        homeCrumb(),
        { label: "Cuenta corriente" },
        { label: "Tipos de movimiento" },
      ],
    },
  },
  {
    path: "/movimientogrupos",
    name: "GrupoMovimiento",
    component: () => import("@/views/GrupoMovimiento.vue"),
    meta: {
      title: "Movimiento por grupo",
      breadcrumbs: [
        homeCrumb(),
        { label: "Cuenta corriente" },
        { label: "Movimiento por grupo" },
      ],
    },
  },
  {
    path: "/cuenta-corriente/personas",
    name: "CuentaCorrientePersonas",
    component: () => import("@/views/CuentaCorrientePersonas.vue"),
    meta: {
      title: "Cuenta corriente - Personas",
      breadcrumbs: [
        homeCrumb(),
        { label: "Cuenta corriente" },
        { label: "Personas" },
      ],
    },
  },
  {
    path: "/ventas",
    name: "Ventas",
    component: () => import("@/views/VentasLista.vue"),
    meta: {
      title: "Ventas",
      breadcrumbs: [...ventasCrumbs()],
    },
  },
  {
    path: "/nueva-venta",
    name: "NuevaVenta",
    component: () => import("@/views/VentaNueva.vue"),
    meta: {
      title: "Nueva venta",
      breadcrumbs: [...ventasCrumbs(), { label: "Nueva venta" }],
    },
  },
  {
    path: "/presupuestos",
    name: "Presupuestos",
    component: () => import("@/views/PresupuestoLista.vue"),
    meta: {
      title: "Presupuestos",
      breadcrumbs: [...presupuestosCrumbs()],
    },
  },
  {
    path: "/presupuestos/nuevo",
    name: "NuevoPresupuesto",
    component: () => import("@/views/PresupuestoForm.vue"),
    meta: {
      title: "Nuevo presupuesto",
      breadcrumbs: [...presupuestosCrumbs(), { label: "Nuevo presupuesto" }],
    },
  },
  {
    path: "/presupuestos/:id",
    name: "DetallePresupuesto",
    component: () => import("@/views/PresupuestoDetalle.vue"),
    meta: {
      title: "Detalle de presupuesto",
      breadcrumbs: [...presupuestosCrumbs(), { label: "Detalle" }],
    },
  },
  {
    path: "/presupuestos/:id/editar",
    name: "EditarPresupuesto",
    component: () => import("@/views/PresupuestoForm.vue"),
    meta: {
      title: "Editar presupuesto",
      breadcrumbs: [...presupuestosCrumbs(), { label: "Editar" }],
    },
  },
  {
    path: "/liquidos-producto",
    name: "LiquidosProducto",
    component: () => import("@/views/LiquidoProductoLista.vue"),
    meta: {
      title: "Líquidos Producto",
      breadcrumbs: [...liquidosProductoCrumbs()],
    },
  },
  {
    path: "/liquidos-producto/lugares",
    name: "LugaresLiquidoProducto",
    component: () => import("@/views/Lugares.vue"),
    meta: {
      title: "Lugares",
      breadcrumbs: [...liquidosProductoCrumbs(), { label: "Lugares" }],
    },
  },
  {
    path: "/liquidos-producto/facturacion-pendiente",
    name: "LiquidosProductoFacturacionPendiente",
    component: () => import("@/views/LiquidoProductoFacturacionPendiente.vue"),
    meta: {
      title: "Items a facturar",
      breadcrumbs: [...liquidosProductoCrumbs(), { label: "Items a facturar" }],
    },
  },
  {
    path: "/liquidos-producto/nuevo",
    name: "NuevoLiquidoProducto",
    component: () => import("@/views/LiquidoProductoNuevo.vue"),
    meta: {
      title: "Nuevo líquido producto",
      breadcrumbs: [...liquidosProductoCrumbs(), { label: "Nuevo" }],
    },
  },
  {
    path: "/liquidos-producto/:id",
    name: "DetalleLiquidoProducto",
    component: () => import("@/views/LiquidoProductoDetalle.vue"),
    meta: {
      title: "Detalle de líquido producto",
      breadcrumbs: [...liquidosProductoCrumbs(), { label: "Detalle" }],
    },
  },
  {
    path: "/liquidos-producto/:id/editar",
    name: "EditarLiquidoProducto",
    component: () => import("@/views/LiquidoProductoNuevo.vue"),
    meta: {
      title: "Editar líquido producto",
      breadcrumbs: [...liquidosProductoCrumbs(), { label: "Editar" }],
    },
  },
  {
    path: "/ventas/:id",
    name: "DetalleVenta",
    component: () => import("@/views/VentaDetalle.vue"),
    meta: {
      title: "Detalle de venta",
      breadcrumbs: [...ventasCrumbs(), { label: "Detalle" }],
    },
  },
  {
    path: "/ventas/:id/editar",
    name: "EditarVenta",
    component: () => import("@/views/VentaEditar.vue"),
    meta: {
      title: "Editar venta",
      breadcrumbs: [...ventasCrumbs(), { label: "Editar" }],
    },
  },
  {
    path: "/compras",
    name: "Compras",
    component: () => import("@/views/ComprasLista.vue"),
    meta: {
      title: "Compras",
      breadcrumbs: [...comprasCrumbs()],
    },
  },
  {
    path: "/compras/nueva",
    name: "NuevaCompra",
    component: () => import("@/views/CompraNueva.vue"),
    meta: {
      title: "Nueva compra",
      breadcrumbs: [...comprasCrumbs(), { label: "Nueva compra" }],
    },
  },
  {
    path: "/compras/:id/editar",
    name: "EditarCompra",
    component: () => import("@/views/CompraNueva.vue"),
    meta: {
      title: "Editar compra",
      breadcrumbs: [...comprasCrumbs(), { label: "Editar compra" }],
    },
  },
  {
    path: "/iva-digital",
    name: "IvaDigital",
    component: () => import("@/views/IvaDigital.vue"),
    meta: {
      title: "IVA Digital",
      breadcrumbs: [...ivaDigitalCrumbs()],
    },
  },
  {
    path: "/proveedores",
    name: "Proveedores",
    component: Clientes,
    meta: {
      title: "Gestión de proveedores",
      breadcrumbs: [homeCrumb(), { label: "Personas" }, { label: "Proveedores" }],
    },
  },
  {
    path: "/remitos",
    name: "Remitos",
    component: () => import("@/views/RemitoLista.vue"),
    props: { tipoDocumento: "Remito" },
    meta: {
      title: "Remitos",
      breadcrumbs: [...remitosCrumbs()],
    },
  },
  {
    path: "/remitos/nuevo",
    name: "NuevoRemito",
    component: () => import("@/views/RemitoNuevo.vue"),
    meta: {
      title: "Nuevo remito",
      breadcrumbs: [...remitosCrumbs(), { label: "Nuevo remito" }],
    },
  },
  {
    path: "/remitos/:id",
    name: "DetalleRemito",
    component: () => import("@/views/RemitoDetalle.vue"),
    meta: {
      title: "Detalle de remito",
      breadcrumbs: [...remitosCrumbs(), { label: "Detalle" }],
    },
  },
  {
    path: "/remitos/:id/editar",
    name: "EditarRemito",
    component: () => import("@/views/RemitoEditar.vue"),
    meta: {
      title: "Editar remito",
      breadcrumbs: [...remitosCrumbs(), { label: "Editar" }],
    },
  },
  {
    path: "/productos",
    name: "Productos",
    component: () => import("@/views/Productos.vue"),
    meta: {
      title: "Productos",
      breadcrumbs: [
        ...ventasCrumbs(),
        { label: "Inventario" },
        { label: "Productos" },
      ],
    },
  },
  {
    path: "/listas-precios",
    name: "ListasPrecios",
    component: () => import("@/views/ListasPrecios.vue"),
    meta: {
      title: "Listas de precios",
      breadcrumbs: [
        ...ventasCrumbs(),
        { label: "Configuración" },
        { label: "Listas de precios" },
      ],
    },
  },
  {
    path: "/amortizaciones",
    name: "Amortizaciones",
    component: () => import("@/views/Amortizaciones.vue"),
    meta: {
      title: "Amortizaciones",
      requiresAmortizaciones: true,
      breadcrumbs: [
        homeCrumb(),
        { label: "Contabilidad" },
        { label: "Amortizaciones" },
      ],
    },
  },
  {
    path: "/categorias",
    name: "Categorias",
    component: () => import("@/views/AbmCategorias.vue"),
    meta: {
      title: "Categorías",
      breadcrumbs: [...parametrosCrumbs(), { label: "Categorías" }],
    },
  },
  {
    path: "/grupos_productos",
    name: "GruposProductos",
    component: () => import("@/views/AbmGruposProductos.vue"),
    meta: {
      title: "Grupos de productos",
      breadcrumbs: [...parametrosCrumbs(), { label: "Grupos de productos" }],
    },
  },
  {
    path: "/puntos-venta",
    name: "PuntosVenta",
    component: () => import("@/views/PuntosVenta.vue"),
    meta: {
      title: "Puntos de venta",
      breadcrumbs: [...parametrosCrumbs(), { label: "Puntos de venta" }],
    },
  },
  {
    path: "/unidades",
    name: "Unidades",
    component: () => import("@/views/AbmUnidades.vue"),
    meta: {
      title: "Unidades de medida",
      breadcrumbs: [...parametrosCrumbs(), { label: "Unidades de medida" }],
    },
  },
  {
    path: "/iva2002",
    name: "Iva2002",
    component: () => import("@/views/AbmIva2002.vue"),
    meta: {
      title: "Categoría IVA 2002",
      breadcrumbs: [...parametrosCrumbs(), { label: "Categoría IVA 2002" }],
    },
  },
  {
    path: "/percepciones-retenciones",
    name: "PercepcionesRetenciones",
    component: () => import("@/views/PercepcionesRetenciones.vue"),
    meta: {
      title: "Percepciones y retenciones",
      breadcrumbs: [...parametrosCrumbs(), { label: "Percepciones y retenciones" }],
    },
  },
  {
    path: "/reporte-recibos",
    name: "ReporteRecibos",
    component: () => import("@/views/ReporteRecibos.vue"),
    meta: {
      title: "Reporte de recibos",
      breadcrumbs: [homeCrumb(), { label: "Reportes" }, { label: "Recibos" }],
    },
  },
  {
    path: "/usuarios",
    name: "Usuarios",
    component: () => import("@/views/UsuariosAdmin.vue"),
    meta: {
      title: "Usuarios",
      requiresAdmin: true,
      breadcrumbs: [
        homeCrumb(),
        { label: "Administración" },
        { label: "Usuarios" },
      ],
    },
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      title: "Ingresar",
      publicPage: true,
    },
  },
  {
    path: "/registrarse",
    name: "Register",
    component: () => import("@/views/Register.vue"),
    meta: {
      title: "Crear cuenta",
      publicPage: true,
    },
  },
  {
    path: "/cuenta-activada",
    name: "AccountActivated",
    component: () => import("@/views/AccountActivated.vue"),
    meta: {
      title: "Cuenta activada",
      publicPage: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const logueado = store.getters.isAuthenticated;
  const isPublic = Boolean(to.meta.publicPage);
  const requiresAdmin = to.matched.some((record) =>
    Boolean(record.meta.requiresAdmin)
  );
  const requiresAmortizaciones = to.matched.some((record) =>
    Boolean(record.meta.requiresAmortizaciones)
  );
  const requiredModule = requiredModuleForPath(to.path);

  if (!logueado && !isPublic) {
    next("/login");
  } else if (requiresAdmin && !store.getters.isAdmin) {
    next("/dashboard");
  } else if (requiresAmortizaciones && !store.getters.canAccessAmortizaciones) {
    next("/dashboard");
  } else if (requiredModule && !store.getters.hasModule(requiredModule)) {
    next("/dashboard");
  } else if (logueado && isPublic && to.path !== "/cuenta-activada") {
    next("/dashboard");
  } else {
    next();
  }
});

router.afterEach((to) => {
  if (!store.getters.isAuthenticated || to.meta.publicPage || to.path === "/dashboard") {
    return;
  }
  const routePattern = to.matched[to.matched.length - 1]?.path;
  if (!routePattern || routePattern === "/") {
    return;
  }
  const routeName = typeof to.name === "string" ? to.name : null;
  void ActivityApi.registerRoute(routePattern, routeName).catch(() => {
    // La telemetría de uso nunca debe bloquear la navegación.
  });
});

export default router;
